/**
 * NITAQ — Domain Types (PHASE 1)
 *
 * هذه الأنواع هي العقد بين الواجهة وبين أي Data Provider.
 * لا تحتوي على أي منطق حسابي — أنواع فقط.
 */

import type { ModelVersion } from './versions';

/** مدينة مدعومة. مصدرها Provider لا الواجهة. */
export type CityName = string;

/** أصل البيانات كما تراه الواجهة: تجريبي أم إنتاجي. */
export type DataOrigin = 'demo' | 'production';

/** درجة الحكم على السعر المطلوب مقابل نطاق القيمة العادلة. */
export type VerdictLevel = 'strong' | 'good' | 'fair' | 'above-market';

/** أنماط الخريطة الحرارية الخمسة المعتمدة في التصميم. */
export type MapMode = 'price' | 'opportunity' | 'momentum' | 'liquidity' | 'pressure';

/** المدد الزمنية المعتمدة لمخطط السعر. */
export type ChartRange = '1M' | '3M' | '6M' | '1Y' | '3Y';

/** الاستخدام المعتمد للأرض في نموذج التقييم. */
export type LandUse = 'residential' | 'commercial' | 'mixed';

/** مستوى مخاطرة الفرص. */
export type RiskAppetite = 'low' | 'medium' | 'high';

/** أفق الاستثمار. */
export type HorizonKey = '1Y' | '3Y' | '5Y';

// ---------------------------------------------------------------------------
// جودة البيانات
// ---------------------------------------------------------------------------

/**
 * وصف حالة البيانات المرافقة لأي مخرَج.
 * الواجهة تعرضها ولا تحسبها.
 */
export interface DataQuality {
  /** أصل البيانات — يُعرض للمستخدم كـ«بيانات تجريبية» عند demo. */
  origin: DataOrigin;
  /** عمر البيانات بالأيام (منذ آخر التقاط). */
  dataAgeDays: number;
  /** عدد الصفقات المماثلة المستخدمة. */
  comparablesCount: number;
  /** الحد الأدنى المتوقع للصفقات المماثلة (٦ في التصميم المعتمد). */
  comparablesExpected: number;
  /** بيانات جزئية — تُعرض بوسم داخل جدول الصفقات المماثلة. */
  partial: boolean;
}

// ---------------------------------------------------------------------------
// الحي
// ---------------------------------------------------------------------------

export interface DistrictRef {
  /** معرّف داخلي مستقر — لا يعتمد على اسم مصدر تجاري. */
  id: string;
  name: string;
  city: CityName;
  /** نطاق جغرافي وصفي داخل المدينة (شمال، شرق...). */
  zone: string;
  lat: number;
  lng: number;
}

/**
 * المؤشرات الست في قسم «تفاصيل السوق».
 *
 * `null` تعني **غير متاح من المصدر** — وليست صفراً ولا قيمة افتراضية.
 * الواجهة ملزمة بعرض «لا توجد بيانات كافية بعد» بدلاً من رقم مخترع.
 */
export interface DistrictMarketDetails {
  /** عدد الصفقات خلال المدة المرجعية. */
  deals90d: number | null;
  /** حجم المعروض (عدد الإعلانات النشطة) — يحتاج مصدر إعلانات. */
  supply: number | null;
  /** متوسط مدة الإعلان بالأيام — يحتاج مصدر إعلانات. */
  avgDaysOnMarket: number | null;
  /** نسبة الإعلانات التي خُفّض سعرها (%) — يحتاج مصدر إعلانات. */
  priceCutRate: number | null;
  /** مؤشر السيولة 0–100. */
  liquidityScore: number | null;
  /** نمو سعر التنفيذ السنوي (%). */
  annualGrowth: number | null;
}

/** نقطة على مخطط السعر: تنفيذ مقابل عرض. */
export interface PricePoint {
  /** ترتيب زمني (0 = الأقدم). */
  t: number;
  /** تسمية زمنية للعرض. */
  label: string;
  /** متوسط سعر التنفيذ ر.س/م². */
  deal: number;
  /** متوسط سعر العرض ر.س/م². */
  ask: number;
}

/** صفقة مسجّلة في الحي. */
export interface DealRecord {
  id: string;
  date: string;
  areaSqm: number;
  pricePerSqm: number;
  totalPrice: number;
  street: string;
}

/**
 * ضغط السوق — قوة المشتري.
 * القيمة 0 = سوق بائع، 100 = سوق مشترٍ. الواجهة تعرض فقط.
 */
export interface MarketPressure {
  buyerPower: number;
  /** جملة تفسيرية جاهزة من الطبقة الخلفية. */
  statement: string;
  modelVersion: ModelVersion;
}

export interface DistrictSummary extends DistrictRef {
  /** متوسط/وسيط سعر التنفيذ ر.س/م² — null إذا لم تكفِ البيانات. */
  dealPricePerSqm: number | null;
  /** متوسط سعر العرض ر.س/م² — يحتاج مصدر إعلانات (null في LIVE حالياً). */
  askPricePerSqm: number | null;
  /** فجوة العرض/التنفيذ (%) — تحتاج سعر عرض. */
  askDealGapPct: number | null;
  /** حدّا نطاق القيمة العادلة للمتر — يحتاجان FV-1.0 (غير منفّذ). */
  fairValueLowPerSqm: number | null;
  fairValueHighPerSqm: number | null;
  /** مؤشر الفرصة 0–100 — يحتاج OP-1.0 (غير منفّذ). */
  opportunityScore: number | null;
  /** الثقة في التقييم 0–100 — تحتاج CF-1.0 (غير منفّذ). */
  confidenceScore: number | null;
  details: DistrictMarketDetails;
  /** ضغط السوق — يحتاج MP-1.0 (غير منفّذ). */
  pressure: MarketPressure | null;
  quality: DataQuality;
}

/** مقاييس وصفية حتمية محسوبة من صفقات حقيقية (LM-1.0). */
export interface DistrictLiveSnapshot {
  transactionCount: number;
  totalTransactionValue: number | null;
  totalArea: number | null;
  medianPricePerSqm: number | null;
  averagePricePerSqm: number | null;
  minPricePerSqm: number | null;
  maxPricePerSqm: number | null;
  latestTransactionDate: string | null;
  priceMovementPct: number | null;
  priceMovementPeriodDays: number | null;
  sufficient: boolean;
  metricsVersion: string;
}

export interface DistrictDetail extends DistrictSummary {
  series: PricePoint[];
  recentDeals: DealRecord[];
  /** موجودة فقط عندما يكون المصدر حقيقياً. */
  live?: DistrictLiveSnapshot | null;
}

// ---------------------------------------------------------------------------
// الخريطة
// ---------------------------------------------------------------------------

export interface MapMetricCell {
  districtId: string;
  name: string;
  city: CityName;
  lat: number;
  lng: number;
  /** قيمة النمط الحالي — null إذا لم تتوفر للحي. */
  value: number | null;
}

export interface MapMetrics {
  mode: MapMode;
  modeLabel: string;
  unit: string;
  /** حدّا المفتاح الحراري — null إذا لم تكفِ البيانات. */
  legendLow: number | null;
  legendHigh: number | null;
  cells: MapMetricCell[];
  quality: DataQuality;
}

// ---------------------------------------------------------------------------
// عقد نتيجة التقييم — Evaluation Contract
// ---------------------------------------------------------------------------

/** مدخلات طلب التقييم. */
/**
 * فئة العقار المقيَّم — بُعد فصل إلزامي.
 * الأرض لا تُقارَن بمبنى: وسيط متر المبنى ≈ 4,950 مقابل ≈ 1,562 للأرض،
 * وخلطهما يسمّم القيمة العادلة للفئتين معاً.
 */
export type PropertyCategory = 'LAND' | 'BUILDING';

export const PROPERTY_CATEGORY_LABEL: Record<PropertyCategory, string> = {
  LAND: 'أرض',
  BUILDING: 'مبنى سكني',
};

/**
 * هل في البيانات صفقات من هذه الفئة؟ تُشتقّ من المخزن لا من الكود،
 * فتتفعّل الفئة وحدها حين تصل صفقاتها.
 */
export type PropertyCategoryAvailability = Record<PropertyCategory, boolean>;

export interface ValuationRequest {
  city: CityName;
  district: string;
  areaSqm: number;
  askingPrice: number;
  /** غيابها = أرض (السلوك السابق على إطلاق المباني). */
  propertyCategory?: PropertyCategory;
  landUse?: LandUse;
  /** عرض الشارع بالمتر — اختياري. */
  streetWidth?: number;
  /**
   * موقع الأرض — **اختياري**. وجوده يرفع دقة اختيار الصفقات المماثلة،
   * وغيابه يجعل التقييم على مستوى الحي فقط. لا يُخمَّن ولا يُشتق أبداً.
   */
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * مستوى دقة التقييم — يُعرض للمستخدم دائماً حتى يعرف على أي أساس بُني الرقم.
 *   PRECISE        — موقع محدد أُدخل، فالمقارنات مرشّحة بالمسافة.
 *   DISTRICT_LEVEL — لا موقع، فالمقارنات على مستوى الحي فقط.
 */
export type LocationMode = 'PRECISE' | 'DISTRICT_LEVEL' | 'DISTRICT_FALLBACK';

/** مكوّن من مكوّنات مؤشر الفرصة (٦ مكوّنات في التحليل الكامل). */
export interface ScoreComponent {
  key: string;
  label: string;
  /** النقاط المحققة. */
  value: number;
  /** السقف الأقصى لهذا المكوّن. */
  max: number;
  /** تعليق قصير جاهز من الطبقة الخلفية. */
  note: string;
}

/** صفقة مماثلة مستخدمة في التقييم. */
export interface Comparable {
  id: string;
  date: string;
  areaSqm: number;
  pricePerSqm: number;
  /** الفرق عن سعر المتر المطلوب (%). */
  deltaPct: number;
  distanceLabel: string;
}

/**
 * EVALUATION CONTRACT — الشكل النهائي لنتيجة التقييم.
 *
 * كل الحقول المطلوبة في PHASE 1 موجودة هنا. القيم في هذه المرحلة تأتي من
 * DemoMarketDataProvider فقط، ومع ذلك يبقى الشكل هو نفسه الذي سينتجه
 * ProductionMarketDataProvider لاحقاً.
 */
export interface ValuationResult {
  property_id: string;
  city: CityName;
  district: string;
  area_sqm: number;
  asking_price: number;
  asking_price_per_sqm: number;
  fair_value_low: number;
  fair_value_high: number;
  /** فرق السعر المطلوب عن منتصف النطاق (%). سالب = أقل من النطاق. */
  difference_percentage: number;
  opportunity_score: number;
  confidence_score: number;
  comparables_count: number;
  /** عمر البيانات بالأيام. */
  data_age: number;
  valuation_model_version: ModelVersion;
  confidence_model_version: ModelVersion;
  /** ISO-8601 */
  generated_at: string;

  // --- حقول عرض مشتقّة من الطبقة الخلفية (ليست حسابات واجهة) ---
  verdict: VerdictLevel;
  /** الجملة الرئيسية جاهزة للعرض. */
  headline: string;
  /** شرح مصدر الثقة. */
  confidence_explanation: string;
  /** أسباب النتيجة (لماذا هذه النتيجة). */
  reasons: string[];
  /** مكوّنات مؤشر الفرصة الستة. */
  score_components: ScoreComponent[];
  /** الصفقات المماثلة. */
  comparables: Comparable[];
  district_id: string;
  quality: DataQuality;
  /** مستوى الدقة الذي بُني عليه هذا التقييم. */
  location_mode: LocationMode;
  /** التسمية الملزمة للنتيجة — تأتي جاهزة من الخادم ولا تُصاغ في الواجهة. */
  scope_label: string;
  /** توضيح الأساس. */
  scope_basis: string;
  /** تحذير إلزامي حين لا يكون التقدير موقعياً — null للحالة الدقيقة. */
  scope_warning: string | null;
  /** تنبيه يُعرض عند توسيع البحث تلقائياً — null إذا لم يحدث توسيع. */
  location_notice?: string | null;
}

// ---------------------------------------------------------------------------
// الفرص
// ---------------------------------------------------------------------------

export interface OpportunityFilter {
  /** رأس المال بالريال. */
  capital: number;
  horizon: HorizonKey;
  risk: RiskAppetite;
  cities: CityName[];
}

export interface Opportunity {
  rank: number;
  districtId: string;
  district: string;
  city: CityName;
  opportunityScore: number;
  /** ٣ أسباب جاهزة من الطبقة الخلفية. */
  reasons: string[];
  /** بيانات Sparkline. */
  spark: number[];
  dealPricePerSqm: number;
  quality: DataQuality;
}

// ---------------------------------------------------------------------------
// المراقبة
// ---------------------------------------------------------------------------

export interface WatchAlertConditions {
  priceDrop: boolean;
  newDeals: boolean;
  supplyChange: boolean;
  opportunityJump: boolean;
}

export interface WatchItem {
  id: string;
  districtId: string;
  district: string;
  city: CityName;
  addedAt: string;
  dealPricePerSqm: number;
  changePct: number;
  opportunityScore: number;
  conditions: WatchAlertConditions;
}

export interface AlertEntry {
  id: string;
  level: 'info' | 'positive' | 'warning';
  text: string;
  at: string;
}

// ---------------------------------------------------------------------------
// نبض السوق والتقارير
// ---------------------------------------------------------------------------

export interface MarketPulseRow {
  label: string;
  value: string;
  tone: 'neutral' | 'up' | 'down' | 'warn';
}

export interface MarketPulse {
  rows: MarketPulseRow[];
  alerts: AlertEntry[];
  quality: DataQuality;
}

export type ReportType = 'district' | 'valuation' | 'portfolio';

export interface ReportSection {
  title: string;
  body: string;
}

export interface ReportPreview {
  id: string;
  type: ReportType;
  title: string;
  subtitle: string;
  generatedAt: string;
  kpis: { label: string; value: string; unit?: string }[];
  sections: ReportSection[];
  /** حدود التقرير — إلزامية في التصميم المعتمد. */
  limitations: string[];
  quality: DataQuality;
}

export interface ReportListItem {
  id: string;
  type: ReportType;
  title: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// اقتراحات البحث
// ---------------------------------------------------------------------------

export interface DistrictSuggestion {
  districtId: string;
  name: string;
  city: CityName;
  dealPricePerSqm: number;
  /** هل يمكن إصدار تقييم في هذا الحي؟ */
  valuationAvailable?: boolean;
  /** سبب غياب التقييم — يُعرض للمستخدم بدل اختفاء الحي. */
  coverageNote?: string | null;
}
